import { useState } from 'react'
import { postAPI, imageLibraryAPI } from '../../../services/api'

export default function DatePostsModal({
  selectedDate,
  selectedDatePosts,
  setSelectedDatePosts,
  keywords,
  account,
  onClose,
  onPostClick,
  onAddPost,
  statusBadge,
  getNewsTime
}) {
  const [createMode, setCreateMode] = useState(null) // null | 'manual' | 'keyword'
  const [createForm, setCreateForm] = useState({ title: '', content: '', imageUrl: '' })
  const [createSaving, setCreateSaving] = useState(false)
  const [createImageFile, setCreateImageFile] = useState(null)
  const [createImagePreview, setCreateImagePreview] = useState(null)
  const [selectedKeywordId, setSelectedKeywordId] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [showLibrary, setShowLibrary] = useState(false)
  const [libraryImages, setLibraryImages] = useState([])
  const [libraryLoading, setLibraryLoading] = useState(false)

  if (!selectedDate) return null

  const handleCreatePost = async () => {
    if (!createForm.title.trim()) {
      alert('제목을 입력해주세요.')
      return
    }
    try {
      setCreateSaving(true)
      const res = await postAPI.create({
        title: createForm.title,
        content: createForm.content,
        imageUrl: createForm.imageUrl,
        accountId: account?.id,
        targetDate: selectedDate || undefined
      })
      if (res.success) {
        let newPost = res.data
        if (createImageFile && newPost.id) {
          try {
            const imgRes = await postAPI.uploadImage(newPost.id, createImageFile)
            if (imgRes.success) newPost = imgRes.data
          } catch (imgErr) {
            alert('이미지 업로드에 실패했습니다.')
          }
        }
        onAddPost(newPost)
        onClose()
      }
    } catch (err) {
      alert('등록 실패: ' + err.message)
    } finally {
      setCreateSaving(false)
    }
  }

  const handleGenerateSingle = async () => {
    if (!account?.id) {
      alert('계정 정보가 필요합니다.')
      return
    }
    try {
      setGenerating(true)
      const res = await postAPI.generateSingle({
        accountId: account.id,
        targetDate: selectedDate || undefined
      })
      if (res.success && res.data) {
        onAddPost(res.data)
        onClose()
      } else {
        alert('생성 실패: ' + (res.message || '알 수 없는 오류'))
      }
    } catch (err) {
      alert('생성 실패: ' + (err.message || '알 수 없는 오류'))
    } finally {
      setGenerating(false)
    }
  }

  const loadLibrary = async () => {
    if (!account?.id) return
    setLibraryLoading(true)
    try {
      const res = await imageLibraryAPI.getMyImages(account.id)
      if (res.success) setLibraryImages(res.data || [])
    } catch (err) {
      console.error('라이브러리 로드 실패:', err)
    } finally {
      setLibraryLoading(false)
    }
  }

  const handleToggleLibrary = () => {
    if (!showLibrary) loadLibrary()
    setShowLibrary(!showLibrary)
  }

  const handleLibrarySelect = (img) => {
    setCreateForm(prev => ({ ...prev, imageUrl: img.imageUrl || img.url }))
    setCreateImagePreview(img.imageUrl || img.url)
    setCreateImageFile(null)
    setShowLibrary(false)
  }

  const handleCreateImageSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setCreateImageFile(file)
    const reader = new FileReader()
    reader.onload = (ev) => setCreateImagePreview(ev.target.result)
    reader.readAsDataURL(file)
  }

  const handleClose = () => {
    setCreateMode(null)
    setCreateForm({ title: '', content: '', imageUrl: '' })
    setCreateImageFile(null)
    setCreateImagePreview(null)
    setSelectedKeywordId(null)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* 모달 헤더 */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="font-bold dark:text-white flex items-center gap-2">
            <span className="material-symbols-outlined text-primary">calendar_today</span>
            {selectedDate}
          </h3>
          <button onClick={handleClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
            <span className="material-symbols-outlined dark:text-white">close</span>
          </button>
        </div>

        <div className="p-4">
          {selectedDatePosts.length === 0 ? (
            <div>
              {!createMode && (
                <div className="text-center py-8">
                  <span className="material-symbols-outlined text-5xl text-slate-300 dark:text-slate-600 mb-3 block">add_photo_alternate</span>
                  <p className="text-slate-500 dark:text-slate-400 mb-1">이 날짜에 등록된 게시물이 없습니다</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mb-6">직접 등록하거나 키워드로 자동 생성할 수 있습니다</p>
                  <div className="flex flex-col gap-3 max-w-xs mx-auto">
                    <button onClick={() => setCreateMode('manual')}
                      className="w-full px-4 py-3 text-sm font-semibold text-white bg-primary rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2">
                      <span className="material-symbols-outlined text-lg">edit_note</span> 직접 등록
                    </button>
                    <button onClick={() => setCreateMode('keyword')}
                      className="w-full px-4 py-3 text-sm font-semibold text-primary bg-primary/10 border border-primary/30 rounded-xl hover:bg-primary/20 transition-colors flex items-center justify-center gap-2">
                      <span className="material-symbols-outlined text-lg">auto_awesome</span> AI 자동 생성
                    </button>
                    <button onClick={() => { handleClose(); /* parent will open rules panel */ }}
                      className="w-full px-4 py-2.5 text-xs font-medium text-slate-500 hover:text-primary transition-colors flex items-center justify-center gap-1">
                      <span className="material-symbols-outlined text-sm">settings</span> 자동 생성 규칙 설정
                    </button>
                  </div>
                </div>
              )}

              {createMode === 'manual' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <button onClick={() => setCreateMode(null)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
                      <span className="material-symbols-outlined text-slate-500">arrow_back</span>
                    </button>
                    <h4 className="font-bold dark:text-white">직접 등록</h4>
                  </div>

                  <div className="relative w-full aspect-video bg-slate-100 dark:bg-slate-800 rounded-xl overflow-hidden">
                    {createImagePreview ? (
                      <img src={createImagePreview} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center">
                        <span className="material-symbols-outlined text-4xl text-slate-300 dark:text-slate-600">image</span>
                        <p className="text-xs text-slate-400 mt-1">이미지를 선택하세요</p>
                      </div>
                    )}
                    <div className="absolute bottom-2 right-2 flex gap-1">
                      <label className="px-3 py-1.5 bg-black/60 hover:bg-black/80 text-white text-xs font-semibold rounded-lg cursor-pointer transition-colors flex items-center gap-1">
                        <span className="material-symbols-outlined text-sm">upload_file</span>
                        파일
                        <input type="file" accept="image/*" className="hidden" onChange={handleCreateImageSelect} />
                      </label>
                      <button onClick={handleToggleLibrary}
                        className="px-3 py-1.5 bg-violet-500/80 hover:bg-violet-500 text-white text-xs font-semibold rounded-lg transition-colors flex items-center gap-1">
                        <span className="material-symbols-outlined text-sm">photo_library</span>
                        AI 라이브러리
                      </button>
                    </div>
                  </div>

                  {/* AI 이미지 라이브러리 */}
                  {showLibrary && (
                    <div className="border border-violet-200 dark:border-violet-800 rounded-xl p-3 bg-violet-50/50 dark:bg-violet-900/20">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-violet-600 dark:text-violet-400">AI 이미지 라이브러리</span>
                        <button onClick={() => setShowLibrary(false)} className="p-0.5 hover:bg-violet-200 dark:hover:bg-violet-800 rounded">
                          <span className="material-symbols-outlined text-sm text-violet-500">close</span>
                        </button>
                      </div>
                      {libraryLoading ? (
                        <div className="text-center py-4 text-xs text-slate-400">로딩 중...</div>
                      ) : libraryImages.length === 0 ? (
                        <div className="text-center py-4 text-xs text-slate-400">생성된 AI 이미지가 없습니다</div>
                      ) : (
                        <div className="grid grid-cols-3 gap-1.5 max-h-40 overflow-y-auto">
                          {libraryImages.map(img => (
                            <div key={img.id} onClick={() => handleLibrarySelect(img)}
                              className="aspect-square rounded-lg overflow-hidden cursor-pointer ring-2 ring-transparent hover:ring-violet-400 transition-all">
                              <img src={img.imageUrl || img.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block">제목</label>
                    <input type="text" value={createForm.title}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="게시물 제목을 입력하세요"
                      className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none" />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block">캡션</label>
                    <textarea value={createForm.content}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, content: e.target.value }))}
                      placeholder="캡션을 입력하세요 (해시태그 포함)" rows={4}
                      className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none resize-none" />
                  </div>

                  <div className="flex gap-2">
                    <button onClick={handleCreatePost} disabled={createSaving}
                      className="flex-1 py-2.5 bg-primary text-white font-semibold text-sm rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-1">
                      {createSaving ? (<><span className="material-symbols-outlined text-sm animate-spin">progress_activity</span> 등록 중...</>) : (<><span className="material-symbols-outlined text-sm">check</span> 등록</>)}
                    </button>
                    <button onClick={() => setCreateMode(null)}
                      className="px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-semibold text-sm rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                      취소
                    </button>
                  </div>
                </div>
              )}

              {createMode === 'keyword' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <button onClick={() => setCreateMode(null)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
                      <span className="material-symbols-outlined text-slate-500">arrow_back</span>
                    </button>
                    <h4 className="font-bold dark:text-white">AI 자동 생성</h4>
                  </div>

                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 text-xs text-slate-500 dark:text-slate-400">
                    <span className="material-symbols-outlined text-sm align-middle mr-1">info</span>
                    자동화 설정의 상호명/업종 정보를 기반으로 AI가 캡션과 이미지를 자동 생성합니다.
                  </div>

                  <div className="flex gap-2">
                    <button onClick={handleGenerateSingle} disabled={generating}
                      className="flex-1 py-2.5 bg-primary text-white font-semibold text-sm rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-1">
                      {generating ? (<><span className="material-symbols-outlined text-sm animate-spin">progress_activity</span> 생성 중...</>) : (<><span className="material-symbols-outlined text-sm">auto_awesome</span> AI 생성</>)}
                    </button>
                    <button onClick={() => setCreateMode(null)}
                      className="px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-semibold text-sm rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                      취소
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">{selectedDatePosts.length}개의 게시물</p>
              {selectedDatePosts.map(post => {
                const badge = statusBadge(post.uploadStatus)
                const time = getNewsTime(post)
                return (
                  <div key={post.id} onClick={() => onPostClick(post)}
                    className="flex gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 cursor-pointer hover:ring-1 hover:ring-primary/50 transition-all">
                    {post.imageUrl ? (
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-slate-200 dark:bg-slate-700">
                        <img src={post.imageUrl} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none' }} />
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-lg flex-shrink-0 bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                        <span className="material-symbols-outlined text-slate-400">image</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold dark:text-white truncate">{post.title || '제목 없음'}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1 mt-0.5">{post.content || ''}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${badge.bg} ${badge.text}`}>{badge.label}</span>
                        {time && <span className="text-[10px] text-slate-400">{time}</span>}
                        {post.sourceType === 'GENERATED' && <span className="text-[10px] text-purple-500 font-semibold">AI</span>}
                      </div>
                    </div>
                    <span className="material-symbols-outlined text-slate-300 dark:text-slate-600 self-center">chevron_right</span>
                  </div>
                )
              })}

              <div className="flex gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                <button onClick={() => { setSelectedDatePosts([]); setCreateMode('manual') }}
                  className="flex-1 py-2 text-xs font-semibold text-primary bg-primary/5 hover:bg-primary/10 rounded-lg transition-colors flex items-center justify-center gap-1">
                  <span className="material-symbols-outlined text-sm">edit_note</span> 직접 등록
                </button>
                <button onClick={() => { setSelectedDatePosts([]); setCreateMode('keyword') }}
                  className="flex-1 py-2 text-xs font-semibold text-primary bg-primary/5 hover:bg-primary/10 rounded-lg transition-colors flex items-center justify-center gap-1">
                  <span className="material-symbols-outlined text-sm">auto_awesome</span> 키워드로 생성
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
